// Copyright 2020 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { CanvasDTO, Dimension, ProjectCanvasCollectionDTO } from "datatypes/SoramaWebApi";
import { Api } from "libraries/WebApi/Api";
import { IPagedRequest, IPageResult, IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { PagedRequest } from "libraries/WebRequestHandler/PagedRequest";
import { IRetrievableRepository } from "./IGenericRepository";

export abstract class IProjectCanvasCollectionRepository implements IRetrievableRepository<ProjectCanvasCollectionDTO> {
    /**
     * Gets the project canvas collection for the provided id.
     * @param id Id of the ProjectCanvasCollection to retrieve.
     */
    public abstract get(id: string): Promise<ProjectCanvasCollectionDTO>;

    /**
     * Gets the canvas pages for the provided ProjectCanvasCollection (id).
     * @param id Id of the ProjectCanvasCollection to retrieve the pages from.
     */
    public abstract getPages(id: string): IPagedRequest<CanvasDTO>;

    /**
     * Creates a new page in the provided ProjectCanvasCollection.
     * @param id Id of the ProjectCanvasCollection to create a new page in.
     * @param size Size of the page, this size is mainly to enforce a certain aspect ratio and should not be coupled to pixel size.
     * @param after Id of the page after which this page should be place
     */
    public abstract createPage(id: string, size: Dimension, after?: string): Promise<CanvasDTO>;

    /**
     * Updates the name field of a ProjectCanvasCollection.
     * @param id Id of the ProjectCanvasCollection to update the name for.
     * @param newName New name for the ProjectCanvasCollection.
     */
    public abstract updateName(id: string, newName: string): Promise<void>;

    /**
     * Delete a project canvas collection (including its pages)
     * THIS CANNOT BE UNDONE!
     * @param id Id of the project canvas collection to delete.
     */
    public abstract delete(id: string): Promise<void>;
}

@autoinject
export class ProjectCanvasCollectionRepository implements IProjectCanvasCollectionRepository {
    constructor(private readonly webRequestHandler: IWebRequestHandler) {}

    public get(id: string): Promise<ProjectCanvasCollectionDTO> {
        return this.webRequestHandler
            .createGetRequest<ProjectCanvasCollectionDTO>(Api.projectCanvasCollections + "/" + id)
            .send()
            .then((x) => x.content);
    }

    public getPages(id: string): IPagedRequest<CanvasDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<CanvasDTO>>(
            Api.projectCanvasCollections + "/" + id + "/Pages"
        );
        return PagedRequest.create(request);
    }

    public createPage(id: string, size: Dimension, after?: string): Promise<CanvasDTO> {
        const queryString = after ? "?after=" + after : "";
        return this.webRequestHandler
            .createPostRequest<CanvasDTO>(Api.projectCanvasCollections + "/" + id + "/Pages" + queryString, <CanvasDTO>{
                size: size,
            })
            .send()
            .then((x) => x.content);
    }

    public updateName(id: string, newName: string): Promise<void> {
        return this.webRequestHandler
            .createPutRequest(Api.projectCanvasCollections + "/" + id + "/Name", newName)
            .withValue()
            .send()
            .then(() => {});
    }

    public delete(id: string): Promise<void> {
        return this.webRequestHandler
            .createDeleteRequest(Api.projectCanvasCollections + "/" + id)
            .send()
            .then(() => {});
    }
}
